import {
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSuppliersOverview } from './fetchSuppliersOverview';
import {
  type SuppliersOverviewQueryParams,
  type SuppliersOverviewResponse,
} from './types';

/**
 * Generates the query options for the suppliers overview query so they can be
 * shared between the useQuery hook and prefetching in route loaders.
 *
 * @param queryParams - Optional filter and pagination parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getSuppliersOverviewQueryOptions = <
  TData = SuppliersOverviewResponse,
>({
  queryParams,
  options,
}: {
  queryParams: Partial<SuppliersOverviewQueryParams>;
  options?: Omit<
    UseQueryOptions<SuppliersOverviewResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: queryParams.recipientCompanyIds?.length
      ? async ({ signal }) => fetchSuppliersOverview(queryParams, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getOverview(queryParams),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });

/**
 * React Query based custom hook for fetching the paginated supplier overview list.
 *
 * All filter parameters are optional. Uses keepPreviousData to prevent list
 * flickering when filters or page changes are applied.
 *
 * @param queryParams - Optional filter and pagination parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing the supplier list.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-overview/operation/get_suppliers_overview_suppliers_views_overview_get
 */
export const useQuerySuppliersOverview = <TData = SuppliersOverviewResponse>(
  queryParams: Partial<SuppliersOverviewQueryParams> = {},
  options?: Omit<
    UseQueryOptions<SuppliersOverviewResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(getSuppliersOverviewQueryOptions<TData>({ queryParams, options }));
